/**
 * Step-type and `_results.<name>` vocabularies DERIVED from the runtime that
 * actually decides the behaviour, not from a list maintained beside the rule.
 *
 * Derivation source: `RUNTIME_ACTION_CONSUMER_CATALOG` (@cogo/action-registry).
 * That artifact is not hand-written — it is produced by INSTANTIATING
 * `ActionFlowEngine`, registering every runtime action pack, and reading
 * `engine.listActionConsumerContracts()`
 * (cogo-runtime-v2/src/action-consumer-projection.ts
 * `collectRuntimeActionConsumerContracts`), and its freshness is gated by
 * `pnpm --filter @cogo/runtime-v2 check:action-consumers`. It is therefore the
 * SAME registry `ActionFlowEngine.resolveBuiltin` consults.
 *
 * Two runtime facts this module encodes, each quoted at its use site:
 *
 * 1. STEP TYPES. `executeStep`'s `default:` arm
 *    (cogo-runtime-v2/src/action-flow-engine.ts:1250-1255) reads
 *
 *        if (this.isSourceTypedActionStep(stepType)) {
 *          await this.executeBasic(this.sourceTypedStepToBasic(step, stepType), context, path)
 *
 *    and `isSourceTypedActionStep` (:1260-1262) is exactly
 *    `Boolean(this.resolveBuiltin(stepType))`. So EVERY canonical action name
 *    and EVERY runtime alias in the catalog is a legal step type. Further,
 *    `sourceTypedStepToBasic` (:1264-1281) lifts every sibling key into
 *    `params`:
 *
 *        for (const [key, value] of Object.entries(record)) {
 *          if (key === "type" || key === "action" || key === "name" || key === "params") continue
 *          params[key] = value
 *        }
 *
 *    so the WHOLE step — not just `step.params` — is the argument surface.
 *
 * 2. `_results.<name>` WRITE PATHS. There are two, and an instrument modelling
 *    only one falsely convicts every producer of the other:
 *      (a) the explicit state-target each contract declares — a `resultChannels`
 *          entry of `kind: "state-target"` naming a `targetParam` (plus that
 *          param's declared read aliases, plus any `defaultTarget` /
 *          `defaultTargetFromParam` used when the param is absent). The
 *          spellings differ per action by declaration: `restApi` →
 *          `resultTarget` (aliases `target`/`responseKey`/`resultKey`),
 *          `storage` → `resultPath`, `setVariable` → `name`.
 *      (b) `step.name` auto-capture — a `resultChannels` entry of
 *          `kind: "return-capture", capture: "step-name"`, executed at
 *          action-flow-engine.ts:1375-1378:
 *
 *            if (result !== undefined && step.name) {
 *              this.state.setState(`_results.${step.name}`, result)
 *            }
 *
 *    `step.key` is deliberately NOT a spelling here: the runtime reads `key`
 *    only as `params.key` (the `storage` key), never as a result binding.
 */
import { RUNTIME_ACTION_CONSUMER_CATALOG } from "@cogo/action-registry"

/**
 * Structural step types — the control-flow node kinds `executeStep` switches on
 * BEFORE reaching its source-typed `default:` arm. `"action"` is the bundle
 * spelling of `"basic"`.
 */
export const STRUCTURAL_STEP_TYPES: ReadonlySet<string> = new Set([
  "basic",
  "action",
  "conditional",
  "expression",
  "loop",
  "parallel",
  "tryCatch",
  "switch",
  "stage",
  "saga",
])

interface StateTargetSpelling {
  /** Param names (declared target param + its declared read aliases). */
  readonly params: readonly string[]
  /** Addresses written when no target param is present. */
  readonly defaults: readonly string[]
  /** Addresses composed at runtime from a param value (`prefix + params[param]`). */
  readonly composedDefaults: readonly { prefix: string; param: string; fallback?: string }[]
}

function buildVocabularies(): {
  actionStepTypes: ReadonlySet<string>
  autoCaptureActions: ReadonlySet<string>
  stateTargets: ReadonlyMap<string, StateTargetSpelling>
} {
  const actionStepTypes = new Set<string>()
  const autoCaptureActions = new Set<string>()
  const stateTargets = new Map<string, StateTargetSpelling>()

  for (const contract of RUNTIME_ACTION_CONSUMER_CATALOG.actions) {
    // SINGLE SITE for "which names may appear as a step type" — mutating this
    // line is what `runtime-step-vocabulary.test.ts` proves goes red.
    const names = [contract.action, ...contract.runtimeAliases]
    const readAliases = new Map(contract.reads.map((r) => [r.path, r.aliases ?? []]))

    let autoCaptures = false
    const params: string[] = []
    const defaults: string[] = []
    const composedDefaults: { prefix: string; param: string; fallback?: string }[] = []
    for (const channel of contract.resultChannels) {
      // SINGLE SITE for write path (b) — the `step.name` auto-capture.
      if (channel.kind === "return-capture" && channel.capture === "step-name") {
        autoCaptures = true
      }
      // SINGLE SITE for write path (a) — the declared explicit state target.
      if (channel.kind === "state-target") {
        if (channel.targetParam) {
          params.push(channel.targetParam, ...(readAliases.get(channel.targetParam) ?? []))
        }
        if (channel.defaultTarget) defaults.push(channel.defaultTarget)
        if (channel.defaultTargetFromParam) composedDefaults.push(channel.defaultTargetFromParam)
      }
    }

    for (const name of names) {
      actionStepTypes.add(name)
      if (autoCaptures) autoCaptureActions.add(name)
      stateTargets.set(name, { params, defaults, composedDefaults })
    }
  }

  return { actionStepTypes, autoCaptureActions, stateTargets }
}

const VOCAB = buildVocabularies()

/**
 * Every name the runtime's `resolveBuiltin` answers to — canonical action names
 * plus runtime aliases. A step whose `type` is one of these is a source-typed
 * action step, NOT an unknown step type.
 */
export const RUNTIME_ACTION_STEP_TYPES: ReadonlySet<string> = VOCAB.actionStepTypes

/**
 * Actions whose contract declares a `return-capture` / `step-name` channel —
 * i.e. the ones for which `_results.<step.name>` is actually populated.
 */
export const RESULT_AUTO_CAPTURE_ACTIONS: ReadonlySet<string> = VOCAB.autoCaptureActions

/** Is `stepType` a step type the runtime accepts (structural or source-typed)? */
export function isRuntimeStepType(stepType: string): boolean {
  return STRUCTURAL_STEP_TYPES.has(stepType) || RUNTIME_ACTION_STEP_TYPES.has(stepType)
}

/** Is `stepType` an action name the runtime dispatches from the `type` field? */
export function isSourceTypedActionStepType(stepType: string): boolean {
  return !STRUCTURAL_STEP_TYPES.has(stepType) && RUNTIME_ACTION_STEP_TYPES.has(stepType)
}

/**
 * The argument surface of a step, as the runtime sees it.
 *
 * Mirrors `sourceTypedStepToBasic` (action-flow-engine.ts:1264-1281): `params`
 * first, then every sibling key except `type`/`action`/`name`/`params`. For a
 * step that is not source-typed this is just `step.params`.
 */
export function stepArgumentSurface(step: Record<string, unknown>): Record<string, unknown> {
  const params = step.params && typeof step.params === "object" && !Array.isArray(step.params)
    ? { ...(step.params as Record<string, unknown>) }
    : {}
  if (typeof step.type !== "string" || !isSourceTypedActionStepType(step.type)) return params
  for (const [key, value] of Object.entries(step)) {
    if (key === "type" || key === "action" || key === "name" || key === "params") continue
    params[key] = value
  }
  return params
}

/**
 * The action a step dispatches: `step.action` for a basic/action step, or
 * `step.type` when the type IS the action name (source-typed).
 */
export function stepDispatchedAction(step: Record<string, unknown>): string | undefined {
  if (typeof step.action === "string" && step.action) return step.action
  if (typeof step.type === "string" && isSourceTypedActionStepType(step.type)) return step.type
  return undefined
}

/**
 * Every `_results.*` address this step's declared state-target channels write.
 *
 * Additive only: callers keep whatever they already accepted. Narrowing the
 * incumbent's unconditional `params.resultTarget` acceptance to the per-action
 * declared spelling would ADD rejections and needs its own measurement.
 */
export function declaredStateTargets(step: Record<string, unknown>): string[] {
  const action = stepDispatchedAction(step)
  if (!action) return []
  const spelling = VOCAB.stateTargets.get(action)
  if (!spelling) return []
  const surface = stepArgumentSurface(step)
  const addresses: string[] = []
  let explicit = false
  for (const param of spelling.params) {
    const value = surface[param]
    if (typeof value === "string" && value) {
      addresses.push(value.replace(/^#/, ""))
      explicit = true
    }
  }
  if (!explicit) {
    addresses.push(...spelling.defaults)
    for (const composed of spelling.composedDefaults) {
      const raw = surface[composed.param]
      const segment = typeof raw === "string" && raw ? raw : composed.fallback
      if (segment !== undefined) addresses.push(`${composed.prefix}${segment}`)
    }
  }
  // A write to `_results.ws.default.connected` makes `_results.ws` and
  // `_results.ws.default` populated too, so every ancestor path counts.
  const withAncestors = new Set<string>()
  for (const address of addresses) {
    const parts = address.split(".")
    for (let i = 1; i <= parts.length; i++) withAncestors.add(parts.slice(0, i).join("."))
  }
  return [...withAncestors]
}

/** Does this step's action auto-capture its return value at `_results.<step.name>`? */
export function autoCapturesStepName(step: Record<string, unknown>): boolean {
  const action = stepDispatchedAction(step)
  return action !== undefined && RESULT_AUTO_CAPTURE_ACTIONS.has(action)
}
